import { useState, useEffect, useCallback } from "react";

import api from "@services/api";
import { useToast } from "@hooks/useToast";
import useTranslation from "src/translations/useTranslation";
import { ToastMessages } from "@shared/languages/interfaces";

import { TravelAnalysisFilter, TravelAnalysisGroupBy, TravelAnalysisGroupedItem, TravelAnalysisGroupedResult } from "../types";

/** Return shape of the `useTravelAnalysisGrouped` hook. */
interface UseTravelAnalysisGroupedReturn {
  byDay: TravelAnalysisGroupedResult | null;
  byPlant: TravelAnalysisGroupedResult | null;
  byRegional: TravelAnalysisGroupedResult | null;
  loading: boolean;
}

/**
 * Fetches grouped success metrics (by day, plant, regional) in parallel.
 * Re-fetches whenever `currentFilter` changes.
 */
const useTravelAnalysisGrouped = (currentFilter: TravelAnalysisFilter | null): UseTravelAnalysisGroupedReturn => {

  const { addToast } = useToast();
  const { t } = useTranslation();

  const [byDay, setByDay] = useState<TravelAnalysisGroupedResult | null>(null);
  const [byPlant, setByPlant] = useState<TravelAnalysisGroupedResult | null>(null);
  const [byRegional, setByRegional] = useState<TravelAnalysisGroupedResult | null>(null);
  const [loading, setLoading] = useState(false);

  /** Fill missing days in a day-grouped result so the chart shows the full range. */
  const fillMissingDays = (result: TravelAnalysisGroupedResult, from: string, to: string): TravelAnalysisGroupedResult => {
    const existingMap = new Map<string, TravelAnalysisGroupedItem>();

    result.items.forEach((item) => existingMap.set(item.label, item));

    const filledItems: TravelAnalysisGroupedItem[] = [];
    const startDate = new Date(from.substring(0, 10) + "T00:00:00");
    const endDate = new Date(to.substring(0, 10) + "T00:00:00");

    const current = new Date(startDate);

    while (current <= endDate) {
      const dateStr = current.toISOString().substring(0, 10);
      const existing = existingMap.get(dateStr);

      if (existing) {
        filledItems.push(existing);
      } else {
        filledItems.push({ label: dateStr, totalAnalyzed: 0, successCount: 0, alertCount: 0, successRate: 0 });
      }

      current.setDate(current.getDate() + 1);
    }

    return { ...result, items: filledItems };
  };

  const fetchGrouped = useCallback(async (filter: TravelAnalysisFilter) => {
    setLoading(true);

    const fetchOne = async (groupBy: TravelAnalysisGroupBy): Promise<TravelAnalysisGroupedResult | null> => {
      try {
        const { data } = await api.post("travels/analysis/success-grouped", { ...filter, groupBy });

        if (data.status === "success") {
          return data.result;
        }

        return null;
      } catch {
        return null;
      }
    };

    try {
      const [dayResult, plantResult, regionalResult] = await Promise.all([
        fetchOne("day"),
        fetchOne("plant"),
        fetchOne("regional")
      ]);

      setByDay(dayResult && filter.from && filter.to ? fillMissingDays(dayResult, filter.from, filter.to) : dayResult);
      setByPlant(plantResult);
      setByRegional(regionalResult);
    } catch {
      addToast({ type: "error", title: t(ToastMessages.error) });
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (currentFilter) {
      fetchGrouped(currentFilter);
    }
  }, [currentFilter, fetchGrouped]);

  return { byDay, byPlant, byRegional, loading };
};

export default useTravelAnalysisGrouped;
